import {isNil} from 'ramda';

import {AI_ASSISTANT_SIDEBAR_WIDTH} from '../constants/AI_ASSISTANT_SIDEBAR_WIDTH';
import type {AiAssistantPanelIntent} from '../types/AiAssistantPanelIntent';
import type {AiAssistantPanelStarter} from '../types/AiAssistantPanelStarter';
import type {AiAssistantPresentation} from '../types/AiAssistantPresentation';
import type {AiAssistantViewMode} from '../types/AiAssistantViewMode';

const INITIAL_PRESENTATION: AiAssistantPresentation = {
  viewMode: 'floating',
  isOpen: false,
  isSidebarOpen: false,
  sidebarWidth: AI_ASSISTANT_SIDEBAR_WIDTH,
  isSidebarResizing: false,
  pendingIntent: null,
  launcherSlot: null,
  panelSlot: null,
  conversationScope: null,
  panelStarters: [],
  panelPrompt: null,
  composerPlaceholder: null,
};

const isSidebarShown = (
  isOpen: boolean,
  viewMode: AiAssistantViewMode,
  panelSlot: HTMLElement | null
) => isOpen && viewMode === 'sidebar' && isNil(panelSlot);

export const createAiAssistantPresentationStore = () => {
  type Listener = () => void;

  const listeners = new Set<Listener>();
  let snapshot = INITIAL_PRESENTATION;
  const getSnapshot = () => snapshot;

  const notify = () => listeners.forEach((listener) => listener());

  const setViewMode = (viewMode: AiAssistantViewMode) => {
    const isSidebarOpen = isSidebarShown(snapshot.isOpen, viewMode, snapshot.panelSlot);

    if (snapshot.viewMode === viewMode && snapshot.isSidebarOpen === isSidebarOpen) {
      return;
    }

    snapshot = {...snapshot, viewMode, isSidebarOpen};
    notify();
  };

  const setOpen = (nextIsOpen: boolean) => {
    const isSidebarOpen = isSidebarShown(nextIsOpen, snapshot.viewMode, snapshot.panelSlot);

    if (snapshot.isOpen === nextIsOpen && snapshot.isSidebarOpen === isSidebarOpen) {
      return;
    }

    snapshot = {...snapshot, isOpen: nextIsOpen, isSidebarOpen};
    notify();
  };

  const setSidebarWidth = (sidebarWidth: number) => {
    if (snapshot.sidebarWidth === sidebarWidth) {
      return;
    }

    snapshot = {...snapshot, sidebarWidth};
    notify();
  };

  const setSidebarResizing = (isSidebarResizing: boolean) => {
    if (snapshot.isSidebarResizing === isSidebarResizing) {
      return;
    }

    snapshot = {...snapshot, isSidebarResizing};
    notify();
  };

  const requestPanelIntent = (intent: AiAssistantPanelIntent) => {
    snapshot = {...snapshot, pendingIntent: intent};
    notify();
  };

  const consumePendingIntent = (): AiAssistantPanelIntent | null => {
    const {pendingIntent} = snapshot;

    if (isNil(pendingIntent)) {
      return null;
    }

    snapshot = {...snapshot, pendingIntent: null};
    notify();
    return pendingIntent;
  };

  const setLauncherSlot = (launcherSlot: HTMLElement | null) => {
    if (snapshot.launcherSlot === launcherSlot) {
      return;
    }

    snapshot = {...snapshot, launcherSlot};
    notify();
  };

  const setPanelSlot = (panelSlot: HTMLElement | null) => {
    if (snapshot.panelSlot === panelSlot) {
      return;
    }

    const isSidebarOpen = isSidebarShown(snapshot.isOpen, snapshot.viewMode, panelSlot);
    snapshot = {...snapshot, panelSlot, isSidebarOpen};
    notify();
  };

  const setConversationScope = (conversationScope: string | null) => {
    if (snapshot.conversationScope === conversationScope) {
      return;
    }

    snapshot = {...snapshot, conversationScope};
    notify();
  };

  const setPanelStarters = (panelStarters: readonly AiAssistantPanelStarter[]) => {
    if (snapshot.panelStarters === panelStarters) {
      return;
    }

    snapshot = {...snapshot, panelStarters};
    notify();
  };

  const setPanelPrompt = (panelPrompt: string | null) => {
    if (snapshot.panelPrompt === panelPrompt) {
      return;
    }

    snapshot = {...snapshot, panelPrompt};
    notify();
  };

  const setComposerPlaceholder = (composerPlaceholder: string | null) => {
    if (snapshot.composerPlaceholder === composerPlaceholder) {
      return;
    }

    snapshot = {...snapshot, composerPlaceholder};
    notify();
  };

  const subscribe = (listener: Listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  return {
    consumePendingIntent,
    getSnapshot,
    requestPanelIntent,
    setComposerPlaceholder,
    setConversationScope,
    setLauncherSlot,
    setOpen,
    setPanelPrompt,
    setPanelSlot,
    setPanelStarters,
    setSidebarResizing,
    setSidebarWidth,
    setViewMode,
    subscribe,
  };
};
